import { Alert, Divider, FormGroup } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IOrganization } from '@frontend/components/interfaces';
import type { IReduxState } from '@frontend/configureStore';
import { getServiceLabel } from '@common/customControls/getServiceLabel';
import {
    FilesUrlBase,
    Gender,
    PractitionerRoleInRequest,
    ProcessEntity,
    ServiceCode,
    ToastType,
    ValidationErrorCode,
} from '@common/enums';
import { ServicePermissionsAuthorizations } from '@common/interfaces/servicePermissions';
import { CancelButton } from '@frontend/components/elements/carePathButtons/CancelButton';
import { FormLabel, HorizontalFormGroup, InputContainer } from '@frontend/components/elements/forms';
import {
    HolterProcedureType,
    TcohFollowingSteps,
} from '@frontend/components/pages/customControls/TCOH/v2/TcohFollowingSteps';
import type { ICustomControlRequestAddEditProps } from '../../interfaces';
import { translateKey } from '../../../../../translate';
import { useAbortableFetch } from '../../../../../utils/hooks/useAbortableFetch';
import { useEpisodeContext } from '../../../../../utils/hooks/useEpisodeContext';
import { showServiceToast } from '../../../../../utils/showToast';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form, FormDivider, ReadOnlyInputText } from '../../../../elements/common';
import { PatientContactAddEdit } from '../../../../elements/PatientContactAddEdit';
import { AssetDropzoneWithActions } from '../../utils/v1/AssetDropzoneWithActions';
import { CareProviderSelector } from '../../utils/v1/CareProviderSelector';
import { NewOrEditRequestProcessContainer } from '../../utils/v1/NewOrEditRequestProcessContainer';
import { ServiceOrganizationProvider } from '../../utils/v1/ServiceOrganizationProvider';
import { ShowHomeHolterInfo } from './ShowHomeHolterInfo';
import { TcohV2RequestAddEditCustomControlFields } from './TcohV2RequestAddEditCustomControlFields';

export function TcohV2RequestAddEdit(props: ICustomControlRequestAddEditProps) {
    const abortableFetch = useAbortableFetch();
    const { patient, isExternalHolter } = useEpisodeContext();
    const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>();
    const [isUploading, setIsUploading] = useState(false);
    const [fileUploadErrorMessage, setFileUploadErrorMessage] = useState<string>();
    const [showFourteenDaysError, setShowFourteenDaysError] = useState<boolean>();
    const [isHomeHolterInfoExpanded, setIsHomeHolterInfoExpanded] = useState(false);
    const [isServiceOrganizationSendingHomeHolter, setIsServiceOrganizationSendingHomeHolter] = useState(false);
    const [homeHolterOrganization, setHomeHolterOrganization] = useState<IOrganization>();
    const {
        servicePermissions: { [ServiceCode.TCOH]: tcohPermissions },
    } = useSelector((state: IReduxState) => state.authentication);

    useEffect(() => {
        if (
            !props.request.customControlData.geslachtHolter &&
            (patient.gender === Gender.male || patient.gender === Gender.female)
        ) {
            props.handleChangeCustomControl({ geslachtHolter: patient.gender });
        }
    }, [props.request.customControlData.geslachtHolter, patient.gender]);

    useEffect(() => {
        if (isExternalHolter === 'false' && !props.request.id) {
            getHomeHolterOrganization();
        }
    }, []);

    useEffect(() => {
        if (props.request.selectedProcedureProviderOrganizationId) {
            checkForServiceOrganizationSendingHomeHolter(
                props.request.selectedProcedureProviderOrganizationId,
                props.request.serviceId
            );
        }

        props.handleChangeCustomControl({
            isServiceOrganizationSendingHomeHolter: isServiceOrganizationSendingHomeHolter,
        });
    }, [props.request.selectedProcedureProviderOrganizationId, isServiceOrganizationSendingHomeHolter]);

    return (
        <NewOrEditRequestProcessContainer
            isExaminationRequest
            isFirstItem={props.isFirstItem}
            itemState={props.itemState}
            process={props.process}
            service={props.service}
            title={
                isServiceOrganizationSendingHomeHolter
                    ? 'Aanvraag Thuis Holter/Event'
                    : getServiceLabel(props.service.code, 'request', props.service.name)
            }
        >
            {addEditForm()}
        </NewOrEditRequestProcessContainer>
    );

    async function saveRequest(saveType: 'draft' | 'final') {
        setIsButtonDisabled(true);
        setShowFourteenDaysError(false);

        if (
            !props.request.customControlData.sendToDeliveryAddress &&
            props.request.customControlData.isServiceOrganizationSendingHomeHolter &&
            saveType === 'final'
        ) {
            await props.handleChangeCustomControl({
                afleveradres_patient: {
                    city: props.request.customControlData.contactgegevens_patient.city,
                    houseNumber: props.request.customControlData.contactgegevens_patient.houseNumber,
                    houseNumberSuffix:
                        props.request.customControlData.contactgegevens_patient.houseNumberSuffix || null,
                    street: props.request.customControlData.contactgegevens_patient.street,
                    zip: props.request.customControlData.contactgegevens_patient.zip,
                },
                sendToDeliveryAddress: props.request.customControlData.sendToDeliveryAddress,
            });
        }

        if (
            tcohPermissions?.canAddAssets &&
            saveType === 'final' &&
            (!props.request.customControlData.files || props.request.customControlData.files.length === 0)
        ) {
            setFileUploadErrorMessage(translateKey('dropzone.addReferralLetter'));
            setIsButtonDisabled(false);

            return;
        }

        const validatedRequest = await props.validateRequest(saveType);
        if (!validatedRequest) {
            setIsButtonDisabled(false);

            return;
        }

        let requestId: string | undefined;
        try {
            requestId = await props.onSaveRequest(saveType);
        } catch (e) {
            if (String(e).includes(ValidationErrorCode.HOLTER_DURATION_CANNOT_BE_FOURTEEN_DAYS)) {
                setIsButtonDisabled(false);
                setShowFourteenDaysError(true);

                return;
            } else {
                throw e;
            }
        }

        setIsButtonDisabled(false);
        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                props.service,
                'request',
                translateKey('toast.submitDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
        }
        props.onSave(requestId);
    }

    function addEditForm() {
        return (
            <Form onSubmit={async () => null}>
                <CareProviderSelector
                    {...props}
                    entity={props.request}
                />

                {getServiceOrganizationProviderSelector()}

                {fileUploadErrorMessage && <Alert appearance="warning">{fileUploadErrorMessage}</Alert>}
                {tcohPermissions?.[ServicePermissionsAuthorizations.canAddAssets] && (
                    <FormGroup
                        hasErrors={Boolean(fileUploadErrorMessage)}
                        isRequired={true}
                        label={translateKey('request.referralLetter')}
                    >
                        <AssetDropzoneWithActions
                            acceptedFiles=".pdf"
                            assets={props.request?.customControlData.files ?? []}
                            assetsBaseUrl={FilesUrlBase.requests}
                            cleanDropzoneAfterCompletingFiles={props.isAutoSaveActive}
                            maxFiles={1}
                            onChange={async (assets) => {
                                await props.handleChangeCustomControl({ files: assets });
                            }}
                            onUploading={async (isUploading) => setIsUploading(isUploading)}
                            processActivityId={props.request.id}
                            service={props.service}
                        />
                    </FormGroup>
                )}

                {isServiceOrganizationSendingHomeHolter && (
                    <ShowHomeHolterInfo
                        isOpen={isHomeHolterInfoExpanded}
                        onClick={() => {
                            setIsHomeHolterInfoExpanded(!isHomeHolterInfoExpanded);
                        }}
                    />
                )}
                {(!isHomeHolterInfoExpanded || !isServiceOrganizationSendingHomeHolter) && <FormDivider />}

                <TcohV2RequestAddEditCustomControlFields
                    customControlData={props.request.customControlData}
                    {...props}
                />

                {showFourteenDaysError && (
                    <Alert appearance="warning">
                        De organisatie die je hebt gekozen maakt gebruik van Bittium recorders, deze recorders kunnen
                        niet worden ingesteld op 14 dagen. Kies een andere opnameduur.
                    </Alert>
                )}

                <FormDivider />

                <PatientContactAddEdit
                    {...props}
                    addressMandatory
                    alertMessage="Controleer of de ingevulde gegevens van de patiënt correct zijn en wijzig ze wanneer nodig."
                    showAddress={isServiceOrganizationSendingHomeHolter}
                    useDeliveryAddress
                />

                <Divider />
                <TcohFollowingSteps
                    holterType={
                        isServiceOrganizationSendingHomeHolter ? HolterProcedureType.Home : HolterProcedureType.External
                    }
                />
                <ButtonContainer
                    alignRight
                    buttons={[
                        <CancelButton
                            cancel={props.onCancel}
                            entity={ProcessEntity.request}
                            isExisting={!!props.request.id}
                            key="cancelButton"
                            revertChanges={props.revertRequestChanges}
                            serviceCode={props.service.code}
                            serviceType={props.service.serviceType}
                        />,
                        <SaveButton
                            buttonDisabled={isButtonDisabled || isUploading}
                            key="saveButton"
                            save={async () => {
                                await saveRequest('draft');
                            }}
                        />,
                        <SaveSendButton
                            buttonDisabled={isButtonDisabled || isUploading}
                            key="saveAndSendButton"
                            saveSend={async () => {
                                await saveRequest('final');
                            }}
                        />,
                    ]}
                />
            </Form>
        );
    }

    async function checkForServiceOrganizationSendingHomeHolter(requesterOrganizationId: string, serviceId: string) {
        const url = `/api/serviceOrganizationSettings?organizationId=${requesterOrganizationId}&serviceId=${serviceId}&isActive=true`;
        const serviceOrganizationSettings = (await abortableFetch.fetchJson(url, 'GET')).json.items;
        if (serviceOrganizationSettings.length > 0) {
            const isSendingHomeHolter = serviceOrganizationSettings[0].isSendingDeviceToPatientsAddress;
            setIsServiceOrganizationSendingHomeHolter(isSendingHomeHolter);
            setIsHomeHolterInfoExpanded(isSendingHomeHolter);
        }
    }

    async function getHomeHolterOrganization() {
        const organization = (
            await abortableFetch.fetchJson('/api/organizations/homeHolterExaminationOrganization', 'GET')
        ).json;
        setHomeHolterOrganization(organization);
        props.handleChange('selectedProcedureProviderOrganizationId', organization.id);
    }

    function getServiceOrganizationProviderSelector() {
        if (isExternalHolter === 'true' && !props.request.id) {
            return (
                <ServiceOrganizationProvider
                    errors={props.errors}
                    initialOrganizationId={props.request.selectedProcedureProviderOrganizationId}
                    initialPractitionerId={props.request.selectedProcedureProviderPractitionerId}
                    isExaminationRequest
                    isExternalHolter={true}
                    onChange={async (organizationId, practitionerId) => {
                        await props.handleChange('selectedProcedureProviderOrganizationId', organizationId);
                        await props.handleChange('selectedProcedureProviderPractitionerId', practitionerId);
                    }}
                    onDoValidate={async () => await props.validateOne('selectedProcedureProviderOrganizationId')}
                    roleOfPractitionersInList={PractitionerRoleInRequest.biometrician}
                    serviceProviderServiceId={props.service.id}
                />
            );
        }
        if (isExternalHolter === 'false' && !props.request.id) {
            return (
                <HorizontalFormGroup>
                    <FormLabel>{translateKey('request.sendTo')}</FormLabel>
                    <InputContainer>
                        <ReadOnlyInputText value={homeHolterOrganization?.name ?? ''} />
                    </InputContainer>
                </HorizontalFormGroup>
            );
        }

        return (
            <ServiceOrganizationProvider
                errors={props.errors}
                initialOrganizationId={props.request.selectedProcedureProviderOrganizationId}
                initialPractitionerId={props.request.selectedProcedureProviderPractitionerId}
                isExaminationRequest
                onChange={async (organizationId, practitionerId) => {
                    await props.handleChange('selectedProcedureProviderOrganizationId', organizationId);
                    await props.handleChange('selectedProcedureProviderPractitionerId', practitionerId);
                }}
                onDoValidate={async () => await props.validateOne('selectedProcedureProviderOrganizationId')}
                roleOfPractitionersInList={PractitionerRoleInRequest.biometrician}
                serviceProviderServiceId={props.service.id}
            />
        );
    }
}
